import {
	Container,
	ContainerPadding,
	Text,
	TokenTextAppearance,
	Image,
	TextAlignment,
	AspectRatioContainer,
	CTA,
	Spinner,
	ContainerGutter,
	TokenTextColor,
	Layout,
	Breakpoints,
	LayoutRowGap,
	Select,
	styled,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { TelefonoInput } from '../../../../oneform/components/telefono-input';
import { DefaultPromotionData, PersonBenefits } from '@esportal/portal-service';
import { PortalContext } from '../../../../../context/portal-context';
import { useErrorAlert } from '../../../../../hooks/use-error-alert';
import { getParamsClubVW } from '../../../../../helpers/get-params-clubvw';
import { ElClasicoIda2627Participado } from './el-clasico-ida-26-27-participado';
import { useBenefitsTracker } from '../../hooks/use-benefits-tracker';

const LayerContentWrapper = styled.div`
	width: 100%;
	max-width: 700px;
	margin: 0 auto;
`;

interface elClasicoIda2627Form {
	name: string;
	data: DefaultPromotionData & {
		equipo: string;
	};
}
export const ElClasicoIda2627Participa: React.FC = () => {
	const { portalService } = React.useContext(PortalContext);
	const { device, tc, url } = getParamsClubVW();
	const [participando, setParticipando] = React.useState<boolean>(false);
	const [estadoParticipacion, setEstadoParticipacion] = React.useState<'participa' | 'participado'>('participa');
	const [phoneData, setPhoneData] = React.useState({ prefix: '+34', phoneNumber: '' });
	const [selectedTeam, setSelectedTeam] = React.useState('');
	const { showAlert } = useErrorAlert();
	const { trackPromotionFormLayerLoad, trackPromotionSubmissionForm } = useBenefitsTracker();

	const promotionName = 'ElClasicoIda2627';

	React.useEffect(() => {
		trackPromotionFormLayerLoad(`vw-club-${promotionName}-enroll`);
	}, []);

	const handleParticipate = () => {
		const participateData: elClasicoIda2627Form = {
			name: promotionName,
			data: {
				phonePrefix: phoneData.prefix,
				phoneMobile: phoneData.phoneNumber,
				originDeviceType: device,
				originTrackingCode: tc,
				formUrl: url,
				equipo: selectedTeam,
			},
		};

		setParticipando(true);

		portalService.postParticipate(participateData).subscribe({
			next: (response: PersonBenefits | null) => {
				setParticipando(false);

				if (response !== null && response.benefits.promotions) {
					const responsePromotion = response.benefits.promotions.find((p) => p.name === promotionName);
					if (responsePromotion?.contestant) {
						setEstadoParticipacion('participado');
						trackPromotionSubmissionForm(`vw-club-${promotionName}-enroll`);
					}
				} else {
					showAlert();
				}
			},
			error: () => {
				setParticipando(false);
				showAlert();
			},
		});
	};

	if (estadoParticipacion === 'participado') {
		return <ElClasicoIda2627Participado />;
	}

	const isFormValid = phoneData.phoneNumber.trim() !== '' && selectedTeam.trim() !== '';

	return (
		<Container
			padding={{
				right: ContainerPadding.dynamic0100,
				bottom: ContainerPadding.dynamic0100,
				left: ContainerPadding.dynamic0100,
			}}
			horizontalAlign={'center'}
			wrap={'always'}
		>
			<AspectRatioContainer data-testid="aspect-ratio-container" ratio="2:1">
				<Image
					src={
						'https://assets.volkswagen.com/is/image/volkswagenag/KV_ElClasico_Portal?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJmRjYzM='
					}
					alt="Imagen El Clasico 2627"
					style={{ borderRadius: '16px', objectFit: 'cover' }}
				/>
			</AspectRatioContainer>

			<LayerContentWrapper>
				<Container
					wrap={'always'}
					gutter={ContainerGutter.dynamic0100}
					padding={{
						top: ContainerPadding.dynamic0130,
						bottom: ContainerPadding.dynamic0100,
					}}
				>
					<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
						Te llevamos a ver{' '}
						<Text bold>
							ELCLÁSICO de LALIGA EA SPORTS FC Barcelona vs. Real Madrid: participa por 1 entrada VIP doble
						</Text>
					</Text>
					<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
						Todos sabemos que, en el fútbol, la victoria no es solo cuestión de suerte. Pero aquí, sí: te damos la
						oportunidad de participar en el{' '}
						<Text bold>
							sorteo de 1 entrada VIP doble para vivir el próximo FC Barcelona vs. Real Madrid del próximo 25 de
							octubre.
						</Text>
					</Text>
					<Text bold appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.center}>
						Para participar, solo necesitas responder:
					</Text>
				</Container>
			</LayerContentWrapper>

			<Container
				wrap={'always'}
				gutter={ContainerGutter.dynamic0050}
				padding={{
					bottom: ContainerPadding.dynamic0100,
				}}
			>
				<Container horizontalAlign={'center'}>
					<Layout
						data-testid="laLigaParticipa__layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: 'a', columns: 24 },
								{ name: 'b', columns: 24 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 10 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 11 },
								{ name: '.', columns: 1 },
							],
						}}
						rowGap={LayoutRowGap.dynamic0100}
					>
						<Container
							wrap={'always'}
							gutter={ContainerGutter.static200}
							padding={{
								top: ContainerPadding.static100,
								right: ContainerPadding.static100,
								bottom: ContainerPadding.static100,
								left: ContainerPadding.static100,
							}}
						>
							<Text textAlign={TextAlignment.center}>¿Cuál es el equipo que siempre llevas en tu corazón?</Text>
							<Select
								label="Equipo*"
								value={selectedTeam}
								onChange={(event: React.SyntheticEvent<HTMLSelectElement>) =>
									setSelectedTeam(event.currentTarget.value)
								}
								isFloating
							>
								<option value="" disabled>
									Equipo
								</option>
								<option value="Athletic Club">Athletic Club</option>
								<option value="Atlético de Madrid">Atlético de Madrid</option>
								<option value="CA Osasuna">CA Osasuna</option>
								<option value="Celta">Celta</option>
								<option value="Deportivo Alavés">Deportivo Alavés</option>
								<option value="Elche CF">Elche CF</option>
								<option value="FC Barcelona">FC Barcelona</option>
								<option value="Getafe CF">Getafe CF</option>
								<option value="Levante UD">Levante UD</option>
								<option value="Málaga CF">Málaga CF</option>
								<option value="R. Racing Club">R. Racing Club</option>
								<option value="Rayo Vallecano">Rayo Vallecano</option>
								<option value="RC Deportivo">RC Deportivo</option>
								<option value="RCD Espanyol de Barcelona">RCD Espanyol de Barcelona</option>
								<option value="Real Betis">Real Betis</option>
								<option value="Real Madrid">Real Madrid</option>
								<option value="Real Sociedad">Real Sociedad</option>
								<option value="Sevilla FC">Sevilla FC</option>
								<option value="Valencia CF">Valencia CF</option>
								<option value="Villarreal CF">Villarreal CF</option>
							</Select>
						</Container>
						<Container
							wrap={'always'}
							gutter={ContainerGutter.static200}
							padding={{
								top: ContainerPadding.static100,
								right: ContainerPadding.static100,
								bottom: ContainerPadding.static100,
								left: ContainerPadding.static100,
							}}
						>
							<Text textAlign={TextAlignment.center}>
								Si la suerte está de tu parte y ganas, ¿a qué número te llamamos?
							</Text>
							<TelefonoInput
								prefix={phoneData.prefix}
								phoneNumber={phoneData.phoneNumber}
								onChange={(data) => setPhoneData(data)}
								required
							/>
						</Container>
					</Layout>
				</Container>
				<Container horizontalAlign={'center'}>
					<CTA
						tag="button"
						aria-label="CTA vouchers redirect"
						size="large"
						disabled={participando || !isFormValid}
						icon={participando ? <Spinner /> : undefined}
						onClick={() => handleParticipate()}
					>
						<Text bold> {participando ? '' : 'Participar'}</Text>
					</CTA>
				</Container>
			</Container>

			<Container horizontalAlign={'center'}>
				<Text
					appearance={TokenTextAppearance.copy100}
					color={TokenTextColor.secondary}
					textAlign={TextAlignment.center}
				>
					Consulta las{' '}
					<CTA
						tag="a"
						aria-label="CTA bases legales"
						size="large"
						emphasis="tertiary"
						onClick={() => {
							window.open(
								'https://www.volkswagen.es/idhub/content/dam/onehub_pkw/importers/es/pdf/VW_Bases-Legales-Promocion-CLUBVWLALIGA_ELCLASICO25OCT.pdf',
								'_blank',
							);
						}}
					>
						<Text>bases legales de la promoción</Text>
					</CTA>{' '}
					para más información.
				</Text>
			</Container>
		</Container>
	);
};
